import { PayloadAction } from '@reduxjs/toolkit';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import { selectUser } from '@/entities/user';
import { GameTypeValue } from '@/shared/config/constants';

import { leaderboardApi } from '../api/leaderboardApi';
import {
  selectLeaderboardGameType,
  selectLeaderboardHasMore,
  selectLeaderboardLoading,
  selectLeaderboardOffset,
  selectLeaderboardPeriod,
  selectLeaderboardSortBy,
} from './selectors';
import { leaderboardActions } from './slice';
import { LeaderboardPeriod, LeaderboardResponse, LeaderboardSortBy, MyRankResponse } from './types';

// ── Fetch a page of leaderboard entries ───────────────────────────────

function* fetchPageSaga(action: PayloadAction<boolean>): Generator {
  const replace = action.payload;
  const period = (yield select(selectLeaderboardPeriod)) as LeaderboardPeriod;
  const sortBy = (yield select(selectLeaderboardSortBy)) as LeaderboardSortBy;
  const gameType = (yield select(selectLeaderboardGameType)) as GameTypeValue;
  const offset = (yield select(selectLeaderboardOffset)) as number;
  const hasMore = (yield select(selectLeaderboardHasMore)) as boolean;
  const loading = (yield select(selectLeaderboardLoading)) as boolean;

  if (!replace && (!hasMore || loading)) return;

  yield put(leaderboardActions.setLoading(true));
  try {
    const res = (yield call(
      leaderboardApi.getLeaderboard,
      period,
      sortBy,
      gameType,
      replace ? 0 : offset,
    )) as LeaderboardResponse;
    yield put(
      leaderboardActions.fetchSuccess({
        entries: res.entries,
        hasMore: res.hasMore,
        replace,
      }),
    );
  } catch {
    yield put(leaderboardActions.setLoading(false));
    if (replace) {
      yield put(leaderboardActions.fetchSuccess({ entries: [], hasMore: false, replace: true }));
    }
  }
}

// ── Fetch current user rank ────────────────────────────────────────────

function* fetchMyRankSaga(): Generator {
  const user = (yield select(selectUser)) as ReturnType<typeof selectUser>;
  if (!user) return;
  const period = (yield select(selectLeaderboardPeriod)) as LeaderboardPeriod;
  const sortBy = (yield select(selectLeaderboardSortBy)) as LeaderboardSortBy;
  const gameType = (yield select(selectLeaderboardGameType)) as GameTypeValue;
  try {
    const res = (yield call(leaderboardApi.getMyRank, period, sortBy, gameType)) as MyRankResponse;
    yield put(leaderboardActions.setMyRank(res));
  } catch {
    yield put(leaderboardActions.setMyRank(null));
  }
}

// ── Reset + refetch when filters change ───────────────────────────────

function* onFilterChangeSaga(): Generator {
  yield put(leaderboardActions.resetEntries());
  yield put(leaderboardActions.fetchPage(true));
  yield* fetchMyRankSaga();
}

// ── Watcher ────────────────────────────────────────────────────────────

export function* leaderboardWatcher(): Generator {
  yield takeLatest(leaderboardActions.setPeriod.type, onFilterChangeSaga);
  yield takeLatest(leaderboardActions.setSortBy.type, onFilterChangeSaga);
  yield takeLatest(leaderboardActions.setGameType.type, onFilterChangeSaga);
  yield takeLatest(leaderboardActions.fetchNextPage.type, function* () {
    yield put(leaderboardActions.fetchPage(false));
  });
  yield takeLatest(leaderboardActions.fetchPage.type, fetchPageSaga);

  // Initial load
  yield put(leaderboardActions.fetchPage(true));
  yield* fetchMyRankSaga();
}
